<template lang="pug">
  .log-steps
    .log-steps-content
      .log-steps-header(v-show="manualEntry")
        | Enter your steps.

      .manual-entry-disabled-text(v-show="!manualEntry")
        template(v-if="!connectedDevice")
          | #[span.status-header No device connected: ] Your step count is taken from a synced device. Please connect a device to track your steps into Be Well app.
        template(v-else)
          | #[span.status-header Note: ] Your step count is taken from your #[span.device-text {{deviceText}}] device.

      .enter-steps-input
        BInput.centered-input(
          type="number"
          name="steps"
          :min="0"
          :max="100000"
          v-model="steps"
          placeholder="0"
          :disabled="!manualEntry"
          :class="{'disabled-manual-entry' : !manualEntry}"
        )
      .save-button(v-if="manualEntry")
        WtButton(
          @click.native="saveEntry"
          :loading="savingSteps"
        ) Next
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogSteps",

  components: {
    WtTitle,
    WtButton,
    WtSvgIcon
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      steps: "",
      savingSteps: false
    };
  },

  computed: {
    ...mapGetters("program", ["programSettingsCopy"]),
    ...mapGetters("members", ["member"]),

    currentMetricValue() {
      // Get steps from walkData or activities - following MakeAnEntryModal pattern
      const walkDataSteps = this._.get(this.entryItem, "todaysSteps", "");
      const activitiesSteps = this._.get(
        this.entryItem,
        "activities[0].steps",
        ""
      );

      // Return walkData steps if available, otherwise activities steps, otherwise empty
      return walkDataSteps || activitiesSteps || "";
    },

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        return (
          this.steps !== this.currentMetricValue || this.formData.has("steps")
        );
      }
      return true;
    },

    manualEntry() {
      return (
        Number(this.programSettingsCopy.manualEntry) === 1 &&
        !this.connectedDevice
      );
    },

    connectedDevice() {
      return (
        (this._.has(this.member, "externalDevice.deviceName") &&
          this.member.externalDevice.deviceName !== "") ||
        this.member.deviceApi === "googlefit" ||
        this.member.deviceApi === "healthconnect" ||
        this.member.deviceApi === "healthkit" ||
        this.member.deviceApi === "fitbit" ||
        this.member.deviceApi === "garmin" ||
        this.member.deviceApi === "samsunghealth"
      );
    },

    deviceText() {
      let deviceApi = this._.get(this.member, "deviceApi", false);
      deviceApi = deviceApi.toString().toLowerCase();
      let deviceName = null;

      if (deviceApi) {
        if (deviceApi === "googlefit") {
          deviceName = "Google Fit";
        }
        if (deviceApi === "healthconnect") {
          deviceName = "Health Connect";
        }
        if (deviceApi === "healthkit") {
          deviceName = "Apple Health";
        }
        if (deviceApi === "garmin") {
          deviceName = "Garmin";
        }
        if (deviceApi === "samsunghealth") {
          deviceName = "Samsung Health";
        }
        if (deviceApi === "fitbit") {
          // get the device type
          deviceName = this._.get(
            this.member,
            "externalDevice.deviceName",
            "Fitbit"
          );
        }
      }

      return deviceName;
    }
  },

  watch: {
    steps() {
      if (this.setFormData) {
        this.formData.set("steps", this.steps);
        this.$emit("formDataUpdated", true);
      }
    },

    entryItem: {
      handler() {
        this.steps = this.currentMetricValue;
      },
      immediate: true,
      deep: true
    },

    // Watch for uniqueKey changes to reset the form when switching dates
    uniqueKey: {
      handler() {
        this.steps = this.currentMetricValue;
      },
      immediate: true
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_steps");
    this.steps = this.currentMetricValue;
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),

    saveEntry() {
      // Always send the value so the backend links the post to this metric
      this.formData.set("steps", this.steps);

      this.savingSteps = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => {
          this.savingSteps = false;
        }
      );
    },

    reset() {
      const item = "steps";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      this.steps = this.currentMetricValue;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.log-steps
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-steps-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center
  gap: 20px

  .log-steps-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20

  .enter-steps-input
    width: 100%

  .save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

.centered-input ::v-deep input
  text-align: center !important
  font-size: 28px
  line-height: 36px
  letter-spacing: -1%
  font-weight: 500
  color: #5D6472

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button
    display: none

.log-steps-form-controls
  margin-top: 20px

.disabled-manual-entry
  border-radius: 10px
  border: 1px solid #B6B8BC

.manual-entry-disabled-text
  width: 100%
  font-weight: 400
  font-size: 15px
  line-height: 23px
  letter-spacing: 0px
  color: #5D6472

.status-header,
.device-text
  font-weight: 700
  font-size: 16px
  line-height: 24px
  letter-spacing: 0px

/deep/ .disabled-manual-entry .input
  background-color: #F1F3F8 !important
</style>
